// CPR API service for processing Bahraini CPR documents
import { processDocument } from './dokiService';
import { validateDocument, VISTA_CASE_TYPES, VISTA_DOCUMENT_TYPES } from './vistaOcrService';
import {
  storeSmartDeviceFile,
  SMART_DEVICE_FILE_KEYS,
} from '../utils/smartDeviceFilesStorage';
import {
  storeHomeFile,
  HOME_FILE_KEYS,
} from '../utils/homeFilesStorage';
import {
  storeTravelFile,
  TRAVEL_FILE_KEYS,
} from '../utils/travelFilesStorage';

/**
 * CPR service for processing Bahraini CPR/ID documents
 * All policies now use Vista OCR - DOKI is deprecated
 */

// CPR DOKI configuration (DEPRECATED - kept for backwards compatibility only)
const CPR_CONFIG = {
  projectId: '95cefcc9-9408-4501-9843-9132e0ed8afa',
  formDataKey: 'CPR-document',
  documentType: 'CPR',
};

class CPRService {
  /**
   * Process CPR files using OCR
   * @param {File} file - The CPR file to process (PDF, JPG, PNG)
   * @returns {Promise<Object>} CPR processing result
   */
  async processCPR(file) {
    // Use the generic DOKI service
    return await processDocument(file, CPR_CONFIG);
  }

  /**
   * Transform CPR data to application format
   * @param {Object} cprData - Raw CPR response data
   * @returns {Object} Formatted passenger data
   */
  transformCPRData(cprData) {
    try {
      const output = cprData.output;

      if (!output || !output.status) {
        throw new Error('CPR processing was not successful');
      }

      const data = output.data || {};

      // Extract CPR data fields based on actual API response structure
      const personalNumber = data.personalNumber || '';
      const nameEnglish = data.nameEnglish || '';
      const dateOfBirth = data.dateOfBirth || '';
      const gender = data.gender || '';
      const nationalityEnglish = data.nationalityEnglish || 'BAHRAINI';
      const expiryDate = data.expiryDate || '';

      // Parse date of birth to component format (expecting YYYY-MM-DD format)
      // Using manual string parsing to avoid timezone issues with Date constructor
      let parsedDateOfBirth = { day: '', month: '', year: '' };
      if (dateOfBirth) {
        try {
          // Split "1994-12-16" directly without Date constructor to avoid timezone shifts
          const parts = dateOfBirth.split('-');
          if (parts.length === 3) {
            parsedDateOfBirth = {
              year: parts[0],
              month: parts[1].padStart(2, '0'),
              day: parts[2].padStart(2, '0'),
            };
          }
        } catch (dobError) {
          console.warn('Could not parse date of birth:', dateOfBirth);
        }
      }

      // Normalize gender values (should already be 'Male' or 'Female')
      let normalizedGender = '';
      if (gender) {
        const genderCapitalized = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
        normalizedGender = genderCapitalized; // 'Male' or 'Female'
      }

      console.log('Transformed CPR data:', {
        personalNumber,
        nameEnglish,
        parsedDateOfBirth,
        normalizedGender,
        nationalityEnglish
      });

      return {
        cprNumber: personalNumber.toString(), // Convert to string
        fullName: nameEnglish,
        dateOfBirth: parsedDateOfBirth,
        gender: normalizedGender,
        nationality: nationalityEnglish.toUpperCase(),
        expiryDate: expiryDate,
        cprProcessed: true,
        cprTimestamp: Date.now(),
        rawData: data, // Keep original data for debugging
      };
    } catch (error) {
      console.error('Error transforming CPR data:', error);
      throw new Error('Failed to process CPR results: ' + error.message);
    }
  }

  /**
   * Validate CPR number format (Bahraini CPR numbers are 9 digits)
   * @param {string} cprNumber - The CPR number to validate
   * @returns {boolean} Whether the CPR number is valid
   */
  validateCPRNumber(cprNumber) {
    if (!cprNumber || typeof cprNumber !== 'string') {
      return false;
    }

    // Remove any spaces or dashes
    const cleanCPR = cprNumber.replace(/[\s-]/g, '');

    // Bahraini CPR numbers are exactly 9 digits
    return /^\d{9}$/.test(cleanCPR);
  }

  /**
   * Format CPR number for display (add dashes if needed)
   * @param {string} cprNumber - The CPR number to format
   * @returns {string} Formatted CPR number
   */
  formatCPRNumber(cprNumber) {
    if (!cprNumber) return '';

    const cleanCPR = cprNumber.replace(/[\s-]/g, '');
    if (cleanCPR.length === 9) {
      // Format as XXX-XXX-XXX
      return `${cleanCPR.slice(0, 3)}-${cleanCPR.slice(3, 6)}-${cleanCPR.slice(6, 9)}`;
    }

    return cprNumber; // Return as-is if not 9 digits
  }
}

// Create singleton instance
const cprService = new CPRService();

/**
 * Process CPR for Smart Device using Vista OCR
 * Uses validate-only mode and stores file for later submission
 * @param {File} file - CPR document file
 * @param {'front'|'back'} side - Which side of the CPR
 * @returns {Promise<Object>} Processing result
 */
export const processSmartDeviceCPR = async (file, side = 'front') => {
  try {
    const documentType = side === 'front'
      ? VISTA_DOCUMENT_TYPES.SMART_DEVICE.CPR_FRONT
      : VISTA_DOCUMENT_TYPES.SMART_DEVICE.CPR_BACK;

    const fileKey = side === 'front'
      ? SMART_DEVICE_FILE_KEYS.CPR_FRONT
      : SMART_DEVICE_FILE_KEYS.CPR_BACK;

    // Use Vista OCR with validate-only mode
    const result = await validateDocument(
      file,
      documentType,
      VISTA_CASE_TYPES.SMART_DEVICE
    );

    if (!result.success) {
      return result;
    }

    // Store file for later Vista submission
    storeSmartDeviceFile(fileKey, file);

    console.log(`[Vista] CPR ${side} processed and stored`);

    return {
      success: true,
      data: result.data,
      rawResponse: result.rawResponse,
    };
  } catch (error) {
    console.error(`[Vista] CPR ${side} processing error:`, error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: `Failed to process CPR ${side}`,
        details: error.message,
      },
    };
  }
};

/**
 * Transform Vista CPR response to application format
 * Works with both DOKI and Vista responses (same structure)
 * @param {Object} cprData - Raw CPR response data
 * @returns {Object} Formatted CPR data
 */
export const transformVistaCPRData = (cprData) => {
  return cprService.transformCPRData(cprData);
};

/**
 * Process CPR for Home Insurance using Vista OCR
 * Uses validate-only mode and stores file for later submission
 * @param {File} file - CPR document file
 * @param {'front'|'back'} side - Which side of the CPR
 * @returns {Promise<Object>} Processing result
 */
export const processHomeCPR = async (file, side = 'front') => {
  try {
    const documentType = side === 'front'
      ? VISTA_DOCUMENT_TYPES.HOME.CPR_FRONT
      : VISTA_DOCUMENT_TYPES.HOME.CPR_BACK;

    const fileKey = side === 'front'
      ? HOME_FILE_KEYS.CPR_FRONT
      : HOME_FILE_KEYS.CPR_BACK;

    // Use Vista OCR with validate-only mode
    const result = await validateDocument(
      file,
      documentType,
      VISTA_CASE_TYPES.HOME
    );

    if (!result.success) {
      return result;
    }

    // Store file for later Vista submission
    storeHomeFile(fileKey, file);

    console.log(`[Vista] Home CPR ${side} processed and stored`);

    return {
      success: true,
      data: result.data,
      rawResponse: result.rawResponse,
    };
  } catch (error) {
    console.error(`[Vista] Home CPR ${side} processing error:`, error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: `Failed to process CPR ${side}`,
        details: error.message,
      },
    };
  }
};

/**
 * Process CPR for Travel Insurance using Vista OCR
 * Uses validate-only mode and stores file for later submission
 * @param {File} file - CPR document file
 * @param {'front'|'back'} side - Which side of the CPR
 * @returns {Promise<Object>} Processing result
 */
export const processTravelCPR = async (file, side = 'front') => {
  try {
    const documentType = side === 'front'
      ? VISTA_DOCUMENT_TYPES.TRAVEL.CPR_FRONT
      : VISTA_DOCUMENT_TYPES.TRAVEL.CPR_BACK;

    const fileKey = side === 'front'
      ? TRAVEL_FILE_KEYS.CPR_FRONT
      : TRAVEL_FILE_KEYS.CPR_BACK;

    // Use Vista OCR with validate-only mode
    const result = await validateDocument(
      file,
      documentType,
      VISTA_CASE_TYPES.TRAVEL
    );

    if (!result.success) {
      return result;
    }

    // Store file for later Vista submission
    storeTravelFile(fileKey, file);

    console.log(`[Vista] Travel CPR ${side} processed and stored`);

    return {
      success: true,
      data: result.data,
      rawResponse: result.rawResponse,
    };
  } catch (error) {
    console.error(`[Vista] Travel CPR ${side} processing error:`, error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: `Failed to process CPR ${side}`,
        details: error.message,
      },
    };
  }
};

/**
 * Process CPR for Personal Accident Insurance using Vista OCR
 * Uses validate-only mode (no file storage needed)
 * @param {File} file - CPR document file
 * @param {'front'|'back'} side - Which side of the CPR
 * @returns {Promise<Object>} Processing result
 */
export const processPersonalAccidentCPR = async (file, side = 'front') => {
  try {
    const documentType = side === 'front'
      ? VISTA_DOCUMENT_TYPES.PERSONAL_ACCIDENT.CPR_FRONT
      : VISTA_DOCUMENT_TYPES.PERSONAL_ACCIDENT.CPR_BACK;

    // Use Vista OCR with validate-only mode
    const result = await validateDocument(
      file,
      documentType,
      VISTA_CASE_TYPES.PERSONAL_ACCIDENT
    );

    if (!result.success) {
      return result;
    }

    console.log(`[Vista] Personal Accident CPR ${side} processed`);

    return {
      success: true,
      data: result.data,
      rawResponse: result.rawResponse,
    };
  } catch (error) {
    console.error(`[Vista] Personal Accident CPR ${side} processing error:`, error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: `Failed to process CPR ${side}`,
        details: error.message,
      },
    };
  }
};

/**
 * Process CPR for Personal Cyber Insurance using Vista OCR
 * Uses validate-only mode (no file storage needed)
 * @param {File} file - CPR document file
 * @param {'front'|'back'} side - Which side of the CPR
 * @returns {Promise<Object>} Processing result
 */
export const processPersonalCyberCPR = async (file, side = 'front') => {
  try {
    const documentType = side === 'front'
      ? VISTA_DOCUMENT_TYPES.PERSONAL_CYBER.CPR_FRONT
      : VISTA_DOCUMENT_TYPES.PERSONAL_CYBER.CPR_BACK;

    // Use Vista OCR with validate-only mode
    const result = await validateDocument(
      file,
      documentType,
      VISTA_CASE_TYPES.PERSONAL_CYBER
    );

    if (!result.success) {
      return result;
    }

    console.log(`[Vista] Personal Cyber CPR ${side} processed`);

    return {
      success: true,
      data: result.data,
      rawResponse: result.rawResponse,
    };
  } catch (error) {
    console.error(`[Vista] Personal Cyber CPR ${side} processing error:`, error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: `Failed to process CPR ${side}`,
        details: error.message,
      },
    };
  }
};

// Export service methods
// Note: processCPR uses legacy DOKI - prefer policy-specific Vista functions above
export const processCPR = (file) => cprService.processCPR(file);
export const transformCPRData = (cprData) => cprService.transformCPRData(cprData);
export const validateCPRNumber = (cprNumber) => cprService.validateCPRNumber(cprNumber);
export const formatCPRNumber = (cprNumber) => cprService.formatCPRNumber(cprNumber);
export default cprService;